import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';

import { PackSlotOdds } from '../PackSlotOdds';
import type { PackOdds_cardPack } from './__generated__/index.graphql';

const SlotWrapper = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--unit);
  opacity: 0.5;
  &.selected {
    opacity: 1;
  }
`;
const PositionIconWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: var(--unit);
  border: 1px solid transparent;
  background:
    linear-gradient(var(--c-black), var(--c-black)) padding-box,
    linear-gradient(180deg, #adadad 0%, #2e2e2e 100%) border-box;
  color: var(--c-nd-700);
  .selected & {
    box-shadow: 0px 0px 40px rgba(var(--c-rgb-white), 0.5);
    color: var(--c-white);
  }
  &:before {
    content: '';
    position: absolute;
    inset: 20%;
    background: rgba(var(--c-rgb-white), 0.5);
    border-radius: 50%;
    z-index: 0;
    filter: blur(10px);
  }
`;

type Props = {
  cardPack: PackOdds_cardPack;
};

export const PackOdds = ({ cardPack }: Props) => {
  const { slots } = cardPack;
  const [selectedSlot, setSelectedSlot] = useState(0);
  const currentSlot = slots[selectedSlot];
  return (
    <Vertical gap={3}>
      <Horizontal gap={4} center>
        {slots.map((slot, index) => (
          <SlotWrapper
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={classNames({
              selected: selectedSlot === index,
            })}
            onClick={() => setSelectedSlot(index)}
          >
            <PositionIconWrapper>
              <PositionIcon position={slot.positionName} size={2.5} />
            </PositionIconWrapper>
            <LabelL bold>{slot.positionShortName}</LabelL>
          </SlotWrapper>
        ))}
      </Horizontal>
      <Vertical gap={2}>
        <LabelL bold>
          <FormattedMessage id="PackOdds.cardOdds" defaultMessage="Card Odds" />
        </LabelL>
        {!!currentSlot && <PackSlotOdds cardPackSlot={currentSlot} />}
      </Vertical>
    </Vertical>
  );
};

PackOdds.fragments = {
  cardPack: gql`
    fragment PackOdds_cardPack on CardPack {
      slug
      slots {
        positionName
        positionShortName
        ...PackSlotOdds_cardPackSlot
      }
    }
    ${PackSlotOdds.fragments.cardPackSlot}
  ` as TypedDocumentNode<PackOdds_cardPack>,
};
